

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Lock, Package, Trophy, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { authFetch } from '@/lib/auth-client';
import { useAuthStore } from '@/stores/auth.store';
import { cn, formatCountdown, getEventTiming } from '@/lib/utils';
import { PACKS_COLLECTION_LIVE } from '@/config/features';
import { transitionOrReduced, transitions } from '@/lib/motion';

interface DashboardEvent {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  sport?: string;
}

// Sport-correct "time until start" phrasing — tip-off is basketball-only.
const START_TERMS: Record<string, string> = {
  NFL: 'to kickoff',
  SOCCER: 'to kickoff',
  SOCCER_EPL: 'to kickoff',
  SOCCER_WORLD_CUP: 'to kickoff',
  SOCCER_UCL: 'to kickoff',
  NBA: 'to tip',
  NCAA_BASKETBALL: 'to tip',
  NHL: 'to puck drop',
};

function startTerm(sport?: string): string {
  return (sport && START_TERMS[sport]) || 'to start';
}

interface StripData {
  lockCount: number;
  unopenedPacks: number;
  nextEvent: DashboardEvent | null;
  /** A card league the user can still join — powers the packs CTA (format picks the copy). */
  joinableBestBall: { eventId: string; title: string; format?: string } | null;
}

/** Cross-promo copy per card-league format (DRAFT_PPR vs Best Ball). */
function joinableLeagueCopy(format?: string): string {
  return format === 'DRAFT_PPR' ? 'PPR is open — pull NFL packs' : 'Best Ball is open — pull NFL packs';
}

const STRIP_ACTION_CLASS =
  'inline-flex h-7 items-center gap-1.5 shrink-0 rounded-md border border-surface-4/60 bg-surface-2/55 px-2 text-cream/90 hover:border-gold/35 hover:bg-gold/10 hover:text-gold transition-colors duration-[var(--duration-micro)]';
const STRIP_PASSIVE_CLASS =
  'inline-flex h-7 items-center gap-1.5 shrink-0 rounded-md border border-surface-4/40 bg-surface-2/35 px-2 text-tertiary';

export function NavContextStrip() {
  const pathname = usePathname();
  const reduceMotion = useReducedMotion();
  const { isAuthenticated, isHydrated, accessToken } = useAuthStore();
  const [data, setData] = useState<StripData | null>(null);
  const [loading, setLoading] = useState(false);

  const hideRoutes =
    pathname.startsWith('/login') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/login/oauth-callback');

  useEffect(() => {
    if (!isHydrated || hideRoutes) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await authFetch('/api/dashboard');
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const events = (json.events || []) as DashboardEvent[];
        const lockedCards = json.lockedCards || [];
        const stats = json.stats || { unopenedPacks: 0 };

        setData({
          lockCount: lockedCards.length,
          unopenedPacks: stats.unopenedPacks ?? 0,
          nextEvent: events.length > 0 ? events[0] : null,
          joinableBestBall: json.joinableBestBall ?? null,
        });
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isHydrated, hideRoutes, accessToken]);

  if (hideRoutes) return null;

  return (
    <div className="border-t border-surface-3/80 bg-surface/90 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1 min-h-[2.25rem] flex items-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={transitionOrReduced(reduceMotion, transitions.context)}
          className={cn(
            'flex items-center gap-2 text-[11px] sm:text-xs text-warm-gray w-full',
            'overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none]',
            '[&::-webkit-scrollbar]:hidden'
          )}
        >
          {!isAuthenticated && (
            <>
              <GuestStripHint hasEvents={!!data?.nextEvent} />
              {data?.nextEvent && (
                <NextArenaLink event={data.nextEvent} className="sm:ml-2" />
              )}
            </>
          )}

          {isAuthenticated && loading && !data && (
            <div className="h-2 w-full max-w-xs rounded-full mint-shimmer opacity-50" />
          )}

          {isAuthenticated && !loading && !data && (
            <Link
              href="/events"
              className="text-tertiary hover:text-gold transition-colors shrink-0"
            >
              Browse live events
            </Link>
          )}

          {isAuthenticated && data && (
            <>
              {/* Locks/packs cluster — card-economy promos stay hidden until
                  PACKS_COLLECTION_LIVE flips (their targets redirect home) */}
              {PACKS_COLLECTION_LIVE && (data.lockCount > 0 ? (
                <Link
                  href="/collection"
                  className={STRIP_ACTION_CLASS}
                >
                  <Lock className="h-3.5 w-3.5 text-gold" />
                  <span>
                    {data.lockCount} card{data.lockCount !== 1 ? 's' : ''} in play
                  </span>
                  <ChevronRight className="h-3 w-3 opacity-50" />
                </Link>
              ) : (
                <span className={cn(STRIP_PASSIVE_CLASS, "hidden sm:inline-flex")}>
                  Cards unlocked
                </span>
              ))}

              {PACKS_COLLECTION_LIVE && (data.unopenedPacks > 0 ? (
                <Link
                  href="/collection?tab=packs"
                  className={STRIP_ACTION_CLASS}
                >
                  <Package className="h-3.5 w-3.5 text-gold" />
                  <span>
                    {data.unopenedPacks} pack{data.unopenedPacks !== 1 ? 's' : ''} unopened
                  </span>
                  <ChevronRight className="h-3 w-3 opacity-50" />
                </Link>
              ) : data.joinableBestBall ? (
                /* Cross-promo: a joinable card league makes packs actionable */
                <Link
                  href="/store?tab=packs"
                  className={STRIP_ACTION_CLASS}
                >
                  <Package className="h-3.5 w-3.5 text-gold" />
                  <span>{joinableLeagueCopy(data.joinableBestBall.format)}</span>
                  <ChevronRight className="h-3 w-3 opacity-50" />
                </Link>
              ) : (
                <span className={cn(STRIP_PASSIVE_CLASS, "hidden md:inline-flex")}>
                  Mint packs
                </span>
              ))}

              {data.nextEvent ? (
                <NextArenaLink event={data.nextEvent} className="sm:ml-auto" />
              ) : (
                <span className={cn(STRIP_PASSIVE_CLASS, "sm:ml-auto")}>
                  <Trophy className="h-3.5 w-3.5 opacity-40" />
                  Season gateway — watch Events
                </span>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function NextArenaLink({
  event,
  className,
}: {
  event: DashboardEvent;
  className?: string;
}) {
  const { label, isLive } = getEventTiming(event.startTime, event.endTime);
  const start = new Date(event.startTime);
  const timing =
    isLive ? label : start > new Date() ? `${formatCountdown(start)} ${startTerm(event.sport)}` : label;

  return (
    <Link
      href={`/events/main/${event.id}`}
      className={cn(
        STRIP_ACTION_CLASS,
        'min-w-0',
        className
      )}
    >
      <Trophy className="h-3.5 w-3.5 text-gold shrink-0" />
      <span className="hidden sm:inline shrink-0">Next arena</span>
      <span className="truncate max-w-[9rem] sm:max-w-[14rem] font-medium text-cream">
        {event.title}
      </span>
      <span className="text-tertiary tabular-nums shrink-0">· {timing}</span>
    </Link>
  );
}

function GuestStripHint({ hasEvents }: { hasEvents: boolean }) {
  return (
    <>
      <Link
        href="/events"
        className={STRIP_ACTION_CLASS}
      >
        <Trophy className="h-3.5 w-3.5 text-gold" />
        <span>{hasEvents ? 'Live arenas — pick an event' : 'Browse events'}</span>
        <ChevronRight className="h-3 w-3 opacity-50" />
      </Link>
      <Link
        href="/store"
        className={cn(STRIP_PASSIVE_CLASS, "hidden sm:inline-flex hover:border-gold/25 hover:bg-gold/10 hover:text-gold transition-colors")}
      >
        The Mint never sleeps
      </Link>
    </>
  );
}
